import { works } from "@/mocks/works";
import type { Work, WorkFilters, SortKey } from "@/types/work";

export const allWorks: Work[] = works;

export const PAGE_SIZE = 12;

export const TYPE_LABELS: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
};

export const SORT_LABELS: Record<SortKey, string> = {
  updated: "最近更新",
  heat: "人气最高",
  rating: "评分最高",
};

export function getWorkById(id: string | undefined): Work | undefined {
  if (!id) return undefined;
  return allWorks.find((work) => work.id === id);
}

export function getFeaturedWorks(): Work[] {
  return allWorks.filter((work) => work.featured);
}

export function formatHeat(heat: number): string {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)} 万`;
  return `${heat}`;
}

export function sortWorks(list: Work[], sortKey: SortKey): Work[] {
  const sorted = [...list];
  if (sortKey === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  } else if (sortKey === "heat") {
    sorted.sort((a, b) => b.heat - a.heat);
  } else {
    sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }
  return sorted;
}

export function filterWorks(list: Work[], filters: Partial<WorkFilters>): Work[] {
  const types = filters.types ?? [];
  const years = filters.years ?? [];
  const regions = filters.regions ?? [];
  const genres = filters.genres ?? [];

  return list.filter((work) => {
    if (types.length > 0 && !types.includes(work.type)) return false;
    if (years.length > 0 && !years.includes(String(work.year))) return false;
    if (regions.length > 0 && !regions.includes(work.region)) return false;
    if (genres.length > 0 && !work.genres.some((genre) => genres.includes(genre))) return false;
    return true;
  });
}

export function searchWorks(list: Work[], keyword: string): Work[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return list.filter((work) => {
    const haystack = [work.title, work.synopsis, work.plot, work.tagline, work.director, ...work.cast, ...work.genres]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function getRelatedWorks(work: Work, limit = 4): Work[] {
  const scored = allWorks
    .filter((item) => item.id !== work.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => work.genres.includes(genre)).length;
      const typeScore = item.type === work.type ? 3 : 0;
      const yearScore = Math.abs(item.year - work.year) <= 2 ? 1 : 0;
      return { item, score: sharedGenres * 2 + typeScore + yearScore };
    })
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat);

  return scored.slice(0, limit).map((entry) => entry.item);
}

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function getYearOptions(list: Work[]): string[] {
  return Array.from(new Set(list.map((work) => String(work.year)))).sort((a, b) => Number(b) - Number(a));
}

export function getRegionOptions(list: Work[]): string[] {
  const order = ["日本", "韩国", "中国香港", "中国台湾"];
  const regions = Array.from(new Set(list.map((work) => work.region)));
  return regions.sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

export function getGenreOptions(list: Work[]): string[] {
  const counts = new Map<string, number>();
  list.forEach((work) => {
    work.genres.forEach((genre) => counts.set(genre, (counts.get(genre) ?? 0) + 1));
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map((entry) => entry[0]);
}